import {reactive, computed, watch} from 'vue';
import _ from "lodash";

// Model for Driver Onboarding - Step 2: Vehicle Information and Registration
// Adds DOM class toggling similar to DispatchUser.js
export default function DriverOnboardingVehicleInfo(data = {}) {
    const state = reactive({
        make: data.make || '',
        model: data.model || '',
        year: data.year || '',
        color: data.color || '',
        plateNumber: data.plateNumber || '',
        vehicleType: data.vehicleType || null,
        capacity: data.capacity || '',
        odometer: data.odometer || '',
        vehicleTypes: data.vehicleTypes || [],
    });

    const validations = {
        isMakeValid: computed(() => !!state.make.trim()),
        isModelValid: computed(() => !!state.model.trim()),
        isColorValid: computed(() => !!String(state.color ?? '').trim()),
        isYearValid: computed(() => {
            const y = parseInt(state.year, 10);
            return Number.isInteger(y) && y >= 1980 && y <= 2027;
        }),
        isPlateValid: computed(() => !!String(state.plateNumber ?? '').trim()),
        isVehicleType: computed(() => !!state.vehicleType),
        isCapacityValid: computed(() => {
            const c = parseInt(state.capacity, 10);
            return Number.isInteger(c) && c >= 1 && c <= 12;
        }),
        isOdometerValid: computed(() => {
            const o = parseInt(state.odometer, 10);
            return Number.isInteger(o) && o >= 0 && o <= 200000;
        }),
    };

    // DOM helpers
    const toggleInvalid = (inputSelector, errorSelector, ok) => {
        const $input = $(inputSelector);
        const $error = $(errorSelector);
        if (!$input || !$input.length) return ok;
        if (ok) {
            $input.removeClass('is-invalid');
            if ($error && $error.length) $error.addClass('d-none');
        } else {
            $input.addClass('is-invalid');
            if ($error && $error.length) $error.removeClass('d-none');
        }
        return ok;
    };

    const validateMake = () => toggleInvalid('#vehicle-make-input', '#vehicle-make-error', validations.isMakeValid.value);
    const validateModel = () => toggleInvalid('#vehicle-model-input', '#vehicle-model-error', validations.isModelValid.value);
    const validateColor = () => toggleInvalid('#vehicle-color-input', '#vehicle-color-error', validations.isColorValid.value);
    const validateYear = () => toggleInvalid('#vehicle-year-input', '#vehicle-year-error', validations.isYearValid.value);
    const validatePlate = () => toggleInvalid('#plate-number-input', '#plate-number-error', validations.isPlateValid.value);
    const validateVehicleType = () => toggleInvalid('#vehicle-type-select', '#vehicle-type-error', validations.isVehicleType.value);
    const validateCapacity = () => toggleInvalid('#vehicle-capacity-input', '#vehicle-capacity-error', validations.isCapacityValid.value);
    const validateOdometer = () => toggleInvalid('#vehicle-odometer-input', '#vehicle-odometer-error', validations.isOdometerValid.value);

    // Watchers
    watch(() => state.make, validateMake);
    watch(() => state.model, validateModel);
    watch(() => state.color, validateColor);
    watch(() => state.year, validateYear);
    watch(() => state.plateNumber, validatePlate);
    watch(() => state.vehicleType, validateVehicleType);
    watch(() => state.capacity, validateCapacity);
    watch(() => state.odometer, validateOdometer);

    const isValid = () => (
        validateMake() |
        validateModel() |
        validateColor() |
        validateYear() |
        validatePlate() |
        validateVehicleType() |
        validateOdometer()
    );

    const toJSON = () => ({
        make: state.make ?? '',
        model: state.model ?? '',
        year: state.year ?? '',
        color: state.color ?? '',
        plateNumber: state.plateNumber ?? '',
        vehicleType: _.isEmpty(state.vehicleType) ? null : state.vehicleType,
        capacity: state.capacity ?? '',
        odometer: state.odometer ?? '',
    });

    return {state, validations, isValid, toJSON};
}
